import {ChangeDetectionStrategy, Component, ContentChild, ElementRef, forwardRef, HostListener, Input, OnDestroy, ViewChild} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {SearchSelectItem} from '../../types/search-select';
import {SearchSelectItemDirective} from '../../directives/search-select-item.directive';
import {ChangeEvent, VirtualScrollerComponent} from 'ngx-virtual-scroller';
import {Subscription} from 'rxjs/Subscription';
import {debounceTime, distinctUntilChanged, tap} from 'rxjs/operators';
import {Subject} from 'rxjs/Subject';

@Component({
    selector: 'search-select',
    templateUrl: './search-select.component.html',
    styleUrls: ['./search-select.component.scss'],
    providers: [{
        useExisting: forwardRef(() => SearchSelectComponent),
        provide: NG_VALUE_ACCESSOR, multi: true
    }],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SearchSelectComponent implements ControlValueAccessor, OnDestroy {
    get visibleItems(): any[] {
        return this._visibleItems;
    }

    set visibleItems(value: any[]) {
        this._visibleItems = value;
    }

    @Input() data: Array<SearchSelectItem> = [];
    @Input() searchProperty: string;
    @Input() placeholder = '';
    @Input() dropup = false;
    @Input() noPadding = false;
    @Input() searchPredicate: (item: SearchSelectItem, searchTerm: string) => boolean;
    @ContentChild(SearchSelectItemDirective) searchSelectItemTemplate: SearchSelectItemDirective;
    @ViewChild('virtualScrollerComponent') virtualScrollerComponent: VirtualScrollerComponent;
    @ViewChild('defaultScrollContainer') defaultScrollContainer: ElementRef;
    showDropdown = false;
    searchStr = '';
    selectedValue: string;
    focusIndex = 0;
    filteredDataArray: Array<SearchSelectItem> = [];
    private _visibleItems: any[] = [];
    focusOnElementSubscription: Subscription;
    focusIndexChanged = new Subject();
    vsStartIndex: number = 0;

    constructor(private _eref: ElementRef) {
        this.focusOnElementSubscription = this.focusIndexChanged
            .pipe(
                debounceTime(100),
                distinctUntilChanged()
            )
            .subscribe(() => this.scrollToFocusedElement());
    }

    get selectedText(): string {
        const selectedItem = this.data.find(i => i.value == this.selectedValue);
        return selectedItem ? selectedItem.optionText : (this.placeholder ? this.placeholder : '');
    }

    get value(): any {
        return this.selectedValue;
    }

    set value(v: any) {
        if (v !== this.selectedValue) {
            this.selectedValue = v;
            this.onChangeCallback(v);
        }
    }

    select() {
        const item = this.filteredDataArray[this.focusIndex];
        this.value = item.value;
        this.showDropdown = false;
    }

    filteredData() {
        this.focusIndex = this.data.findIndex(item => item.value == this.value);
        const filterFn = item => typeof this.searchPredicate === 'function' ? this.searchPredicate(item, this.searchStr) : this.defaultSearchPredicate(item);
        this.filteredDataArray =
            this.searchStr
                ?
                this.data.filter(filterFn)
                :
                this.data;
    }

    toggleDropdown(searchInput) {
        if (this.data.length) {
            this.showDropdown = !this.showDropdown;
            if (this.showDropdown) {
                setTimeout(() => {
                    searchInput.focus();
                }, 100);
                this.filteredData();
            }
        }
    }

    onKeydownArrowUp() {
        if (this.focusIndex > 0) this.focusIndex--;
        this.focusIndexChanged.next(this.focusIndex);
    }


    onKeydownArrowDown() {
        if (this.focusIndex < this.filteredDataArray.length - 1) this.focusIndex++;
        this.focusIndexChanged.next(this.focusIndex);
    }

    onKeydownEnter(e) {
        this.select();
        e.preventDefault();
    }

    writeValue(value: any) {
        if (value !== this.selectedValue) {
            this.selectedValue = value;
        }
    }

    private onTouchedCallback: () => void = () => {
    };
    private onChangeCallback: (_: any) => void = () => {
    };

    get noResultsMatchText() {
        return `No results match "${this.searchStr}"`;
    }

    registerOnChange(fn: any) {
        this.onChangeCallback = fn;
    }

    registerOnTouched(fn: any) {
        this.onTouchedCallback = fn;
    }

    private defaultSearchPredicate(item: SearchSelectItem) {
        const searchString = item[this.searchProperty ? this.searchProperty : 'optionText'];
        if (typeof searchString !== 'string' && item.value !== '') {
            console.warn(`provided object does not have required property ${this.searchProperty}`, item);
        }
        return (searchString || '').toLowerCase().indexOf(this.searchStr.toLowerCase()) !== -1;
    }

    @HostListener('document:click', ['$event.target'])
    hideDropdown(targetElement: HTMLElement): void {
        if (!this._eref.nativeElement.contains(targetElement)) this.showDropdown = false;
    }

    trackByValue(index: number, item: SearchSelectItem) {
        return item.value;
    }

    private scrollToFocusedElement() {
        if (this.virtualScrollerComponent && this.searchSelectItemTemplate) {
            this.virtualScrollerComponent.scrollToIndex(this.focusIndex, true);
        } else if (this.defaultScrollContainer) {
            const defaultScrollContainer: HTMLUListElement = this.defaultScrollContainer.nativeElement;
            const el = this.filteredDataArray[this.focusIndex];
            const elRef = defaultScrollContainer.querySelector(`#item-${el.value}`);
            elRef.scrollIntoView();
        }
    }

    ngOnDestroy(): void {
        if (this.focusOnElementSubscription) {
            this.focusOnElementSubscription.unsubscribe();
        }
    }

    isFocused(i: number) {
        return (i + this.vsStartIndex) === this.focusIndex;
    }

    focus(i: number) {
        this.focusIndex = i;
    }

    startChanged($event: ChangeEvent) {
        this.vsStartIndex = $event.start;
    }
}



// WEBPACK FOOTER //
// /app/src/app/components/search-select/search-select.component.ts